import { Runtime } from "@morphism/fp";
import React from "react";

import { NewBranch as GenericBranch } from "~/Branch";
import { gql } from "~/Graph";
import { ID } from "~/ID";

export type Location = {
  readonly location: Partial<ID & Location.GPS & Location.Address>;
};

export namespace Location {
  export namespace Fragment {
    export const document = () => gql`
      fragment Location on Location__HasOneMaybe {
        location {
          id
          gps {
            latitude
            longitude
          }
        }
      }
    `;
  }

  export namespace Response {
    export const definition = () =>
      Runtime.strict({ location_id: Runtime.nullable(Runtime.number()) });

    export const decode = (
      encoded: Runtime.ToType<typeof definition>
    ): Location => ({ location: encoded.location_id ? { ...ID.from(encoded.location_id) } : { } });
  }

  export type GPS = {
    readonly gps: GPS.Decimal.Latitude & GPS.Decimal.Longitude;
  };

  export function GPS(gps: GPS) {
    return <>{GPS.toString(gps)}</>;
  }

  export namespace GPS {
    export const toString = ({ gps: { latitude, longitude } }: GPS): string =>
      `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;

    export type Decimal = Decimal.Latitude | Decimal.Longitude;
    export namespace Decimal {
      export type Latitude = { readonly latitude: number };
      export type Longitude = { readonly longitude: number };

      export const toDMS = (decimal: Decimal): DMS => {
        const [isLatitude, value] =
          "latitude" in decimal
            ? [true, decimal.latitude]
            : [false, decimal.longitude];

        const absolute = Math.abs(value);
        const degrees = Math.floor(absolute);
        const minutes = (absolute - degrees) * 60;
        const minutesFloored = Math.floor(minutes);
        const seconds = (minutes - minutesFloored) * 60;
        const direction = isLatitude
          ? degrees >= 0
            ? "N"
            : "S"
          : degrees >= 0
          ? "E"
          : "W";

        return { degrees, minutes, seconds, direction };
      };
    }

    export type DMS = {
      readonly degrees: number;
      readonly minutes: number;
      readonly seconds: number;
      readonly direction: "N" | "E" | "S" | "W";
    };

    export namespace DMS {
      export const toString = ({
        degrees,
        minutes,
        seconds,
        direction,
      }: DMS): string =>
        pipe(
          [
            degrees.toFixed(2),
            minutes.toFixed(2),
            seconds.toFixed(2),
            direction,
          ],
          ([degrees, minutes, seconds, direction]) =>
            `${degrees}°${minutes}'${seconds}" ${direction}`
        );
    }
  }

  export type Address = {
    readonly address: Partial<State> & {
      readonly lines?: readonly [string, ...(readonly string[])];
      readonly city?: string;
      readonly zip?: number;
    };
  };

  export type AddressProps = Address & { readonly labelPrefix?: string };

  export function Address({ address, labelPrefix }: AddressProps) {
    const addressName = Address.toString({ address });

    return (
      <div data-testid="location-address-tooltip-wrapper">
        {labelPrefix && addressName
          ? GenericBranch.TooltipTitle(labelPrefix, addressName)
          : addressName}
      </div>
    );
  }

  export namespace Address {
    export const toString = ({
      address: { lines, city, state, zip },
    }: Address): string | undefined => {
      const beforeComma = (lines ?? []).join(" ");
      const afterComma = [city, state?.name, zip]
        .filter((it) => !!it)
        .join(" ");

      const address = [beforeComma, afterComma].join(", ");
      return address === "" ? undefined : address;
    };

    export namespace Response {
      export const runtime = () =>
        Runtime.strict({
          street_1: Runtime.nullable(Runtime.string()),
          street_2: Runtime.nullable(Runtime.string()),
          city: Runtime.nullable(Runtime.string()),
          zip_code: Runtime.nullable(Runtime.number()),
          state: Runtime.nullable(State.Response.runtime()),
        });

      export const decode = (
        response: Runtime.ToType<typeof runtime>
      ): Address | undefined => {
        const lines = !response.street_1
          ? undefined
          : ([response.street_1, ...(response.street_2 ?? [])] as const);

        const city = response.city ?? undefined;
        const zip = response.zip_code ?? undefined;
        const state = response.state
          ? State.Response.decode(response.state)
          : undefined;

        return lines || city || zip || state
          ? { address: { ...state, lines, city, zip } }
          : undefined;
      };
    }
  }

  export type State = {
    readonly state: Partial<ID & State.Abbreviation> & {
      readonly name?: string;
    };
  };

  export namespace State {
    export namespace Response {
      export const runtime = () =>
        Runtime.strict({
          state_id: Runtime.nullable(Runtime.number()),
          abbreviation: Runtime.nullable(Runtime.string()),
          name: Runtime.nullable(Runtime.string()),
        });

      export const decode = (
        response: Runtime.ToType<typeof runtime>
      ): State => ({
        state: {
          ...(response.state_id && ID.fromNumber(response.state_id)),
          abbreviation: response.abbreviation ?? undefined,
          name: response.name ?? undefined,
        },
      });
    }

    export type Abbreviation = { readonly abbreviation: string };
    export namespace Abbreviation {
      export const list = () =>
        [
          "AK",
          "AL",
          "AR",
          "AZ",
          "CA",
          "CO",
          "CT",
          "DE",
          "FL",
          "GA",
          "HI",
          "IA",
          "ID",
          "IL",
          "IN",
          "KS",
          "KY",
          "LA",
          "MA",
          "MD",
          "ME",
          "MI",
          "MN",
          "MO",
          "MS",
          "MT",
          "NC",
          "ND",
          "NE",
          "NH",
          "NJ",
          "NM",
          "NV",
          "NY",
          "OH",
          "OK",
          "OR",
          "PA",
          "RI",
          "SC",
          "SD",
          "TN",
          "TX",
          "UT",
          "VA",
          "VT",
          "WA",
          "WI",
          "WV",
          "WY",
        ] as const;
    }
  }
}
